import React, { Suspense, useEffect, useState, useLayoutEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n, { initializeI18n } from './i18n';

// Import i18n first to ensure it initializes before the app renders
import './i18n';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import DashboardLayout from './layouts/DashboardLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import UsersList from './pages/users/UsersList';
import UserForm from './components/users/UserForm';
import Profile from './pages/Profile';
import DriverRoutes from './routes/DriverRoutes';
import ShipmentRoutes from './routes/shipmentRoutes';
import ZoneRoutes from './routes/zoneRoutes';
import CustomerRoutes from './pages/customers/customerRoutes';
import QuoteRoutes from './routes/quoteRoutes';
import StaffManagement from './components/StaffManagement';
import { CircularProgress, Box, styled } from '@mui/material';

// Import the theme provider
import CustomThemeProvider from './theme/ThemeProvider';
import { REGIONAL_CONFIG } from './config/regional';

// Wait for i18n to be initialized
const I18nInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isI18nReady, setIsI18nReady] = React.useState(i18n.isInitialized);

  React.useEffect(() => {
    if (!i18n.isInitialized) {
      i18n.init().then(() => setIsI18nReady(true));
    }
  }, []);

  if (!isI18nReady) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};

// Loading component for suspense fallback
const Loader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const SuspenseLoader = () => (
  <Loader>
    <CircularProgress />
  </Loader>
);

const App: React.FC = () => {
  // Set the default locale and timezone for Burkina Faso
  useLayoutEffect(() => {
    // Set the document language
    document.documentElement.lang = REGIONAL_CONFIG.dateTime.locale;
    
    // Set the timezone (note: this is just for display, actual timezone handling should be done on the server)
    if (process.env.NODE_ENV === 'development') {
      console.log('Setting default locale to:', REGIONAL_CONFIG.dateTime.locale);
    }
    
    // Set the default meta description with regional info
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', `Admin Dashboard - Serving ${REGIONAL_CONFIG.currency.code} region`);
    
    // Cleanup function
    return () => {
      // Any cleanup if needed
    };
  }, []);

  const [isI18nReady, setIsI18nReady] = useState(i18n.isInitialized);

  useEffect(() => {
    const init = async () => {
      if (!i18n.isInitialized) {
        await initializeI18n();
      }
      setIsI18nReady(true);
    };

    init();
  }, []);

  if (!isI18nReady) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <CustomThemeProvider>
        <AuthProvider>
          <LanguageProvider>
            <BrowserRouter>
              <Suspense fallback={<SuspenseLoader />}>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <DashboardLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="dashboard" element={<Dashboard />} />
                    <Route path="profile" element={<Profile />} />
                    <Route path="users">
                      <Route index element={
                        <RoleBasedRoute allowedRoles={['admin']}>
                          <UsersList />
                        </RoleBasedRoute>
                      } />
                      <Route path="new" element={
                        <RoleBasedRoute allowedRoles={['admin']}>
                          <UserForm />
                        </RoleBasedRoute>
                      } />
                      <Route path=":id" element={
                        <RoleBasedRoute allowedRoles={['admin']}>
                          <UserForm />
                        </RoleBasedRoute>
                      } />
                    </Route>
                    <Route path="drivers/*" element={<DriverRoutes />} />
                    <Route path="shipments/*" element={
                      <RoleBasedRoute allowedRoles={['admin']}>
                        <ShipmentRoutes />
                      </RoleBasedRoute>
                    } />
                    <Route path="zones/*" element={
                      <RoleBasedRoute allowedRoles={['admin']}>
                        <ZoneRoutes />
                      </RoleBasedRoute>
                    } />
                    <Route path="customers/*" element={
                      <RoleBasedRoute allowedRoles={['admin']}>
                        <CustomerRoutes />
                      </RoleBasedRoute>
                    } />
                    <Route path="quotes/*" element={
                      <RoleBasedRoute allowedRoles={['admin']}>
                        <QuoteRoutes />
                      </RoleBasedRoute>
                    } />
                    <Route path="staff" element={
                      <RoleBasedRoute allowedRoles={['admin']}>
                        <StaffManagement />
                      </RoleBasedRoute>
                    } />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
          </LanguageProvider>
        </AuthProvider>
      </CustomThemeProvider>
    </I18nextProvider>
  );
}

export default App;
